// ─────────────────────────────────────────────────────────────────────────────
// StatusPage, the public service status page at /status.
//
// It keeps the shape a status page is expected to have, the kit's <StatusPill>
// and <UptimeBar>, but it does not claim anything that is not measured. There
// is no published uptime percentage and no incident history, because the
// monitoring feed behind them is not published yet. The timeline is therefore
// drawn empty on purpose, with a legend that says so, and the incident section
// carries an honest empty state plus the shape every future post will take.
//
// Laid out to the shared marketing kit: left-aligned headings that end in a
// period, hairline borders, monochrome states, and a captioned grey <MediaSlot>
// where the diagram belongs.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_SECONDARY,
    Icon,
    Container,
    Section,
    Body,
    MediaSlot,
    SectionHead,
    FeatureRow,
    Card,
    SpecList,
    StatusPill,
    UptimeBar,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
    SITE_URL,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// Every component and the state it is in right now. States come from the kit:
// operational, degraded, maintenance, outage.
const COMPONENTS = [
    { name: 'Dashboard', desc: 'The web workspace and every module inside it', state: 'operational' },
    { name: 'API', desc: 'The endpoints the workspace and the companion apps read and write', state: 'operational' },
    { name: 'Sign in', desc: 'Account sign-in and session refresh', state: 'operational' },
    { name: 'Store sync', desc: 'Order and product ingestion from a connected storefront', state: 'operational' },
    { name: 'Processor reporting', desc: 'Card transaction and settlement batch retrieval', state: 'operational' },
    { name: 'Statement import', desc: 'Bank statement uploads and the actual-deposits ledger', state: 'operational' },
    { name: 'Companion apps', desc: 'Register, Scale, Packing, Receiving and Delivery', state: 'operational' },
    { name: 'Exports', desc: 'CSV, Excel, PDF and the accounting journal file', state: 'operational' },
    { name: 'Notifications', desc: 'Push and in-app alerts to store staff', state: 'operational' },
];

// The kit's palest cell stands in for a day with no record. Nothing is measured
// yet, so every cell is drawn in it: the timeline is honest rather than full of
// black bars nobody recorded.
const NO_RECORD = Object.fromEntries(Array.from({ length: 90 }, (_, i) => [i, 'outage']));

export default function StatusPage() {
    return (
        <MarketingShell>
            <Seo
                title="System status and service availability"
                description="The current state of every Wavelength component: dashboard, API, sign-in, store sync, processor reporting, exports and the apps. Uptime reporting starts soon."
                path="/status"
                keywords="Wavelength status, system status page, service availability, back office uptime, incident reports, retail software status"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': `${SITE_URL}/status`,
                            url: `${SITE_URL}/status`,
                            name: 'System status',
                            description: 'The current state of each Wavelength component. Historical uptime reporting begins once the monitoring feed is published.',
                            publisher: { '@id': `${SITE_URL}/#organization` },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/overview` },
                                { '@type': 'ListItem', position: 2, name: 'System status', item: `${SITE_URL}/status` },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero, with the current state beside the eyebrow ─────────── */}
            <PageHero
                eyebrow={
                    <span className="inline-flex flex-wrap items-center gap-3">
                        System status
                        <StatusPill state="operational" label="All components operational" />
                    </span>
                }
                title="All systems operational."
                lead="The current state of every part of Wavelength. This page is kept by hand today, so it says what is running and nothing more. Published uptime figures and incident history start when the monitoring feed goes live."
                primary={{ label: 'Report a problem', to: '/contact' }}
                secondary={{ label: 'How it is built', to: '/security' }}
            >
                <Reveal>
                    <Card pad={false}>
                        <div className="flex items-center justify-between border-b border-[rgba(10,10,10,0.08)] px-6 py-4">
                            <span className="text-[13px] font-medium text-[#0A0A0A]">Component</span>
                            <span className="text-[12px] text-[#A1A1A1]">Current state</span>
                        </div>
                        <div>
                            {COMPONENTS.map((c, i) => (
                                <div
                                    key={c.name}
                                    className={`flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''}`}
                                >
                                    <div className="min-w-0">
                                        <div className="text-[15px] font-medium text-[#0A0A0A]">{c.name}</div>
                                        <div className="mt-0.5 text-[13px] leading-[1.5] text-[#8A8A8A]">{c.desc}</div>
                                    </div>
                                    <div className="flex-none">
                                        <StatusPill state={c.state} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </Reveal>
            </PageHero>

            {/* ── Uptime, deliberately empty ─────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Uptime history."
                            lead="No percentage is claimed here, because none has been measured and published yet. The timeline below is empty by design: every day is drawn as a day with no record, and it fills in from the day monitoring is published forward."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <Card className="mt-12">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
                                <span className="text-[15px] font-medium text-[#0A0A0A]">All components</span>
                                <span className="text-[13px] text-[#8A8A8A]">Last 90 days, no days recorded</span>
                            </div>
                            <div className="mt-5">
                                <UptimeBar days={90} incidents={NO_RECORD} />
                                <div className="mt-2 flex items-center justify-between text-[11px] text-[#A1A1A1]">
                                    <span>90 days ago</span>
                                    <span>Today</span>
                                </div>
                            </div>
                            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-[rgba(10,10,10,0.08)] pt-5 text-[12px] text-[#8A8A8A]">
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-2.5 w-2.5 rounded-[2px] bg-[#0A0A0A]" /> Recorded day, no interruption
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-2.5 w-2.5 rounded-[2px] bg-[#A3A3A3]" /> Recorded day, degraded
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-2.5 w-2.5 rounded-[2px] bg-[#D4D4D4]" /> No record
                                </span>
                            </div>
                        </Card>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Incidents: an honest empty state, plus the shape to come ── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Incident history."
                            lead="Nothing has been published here. When something does go wrong, the post goes up on this page rather than into an email nobody can find later."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr] lg:gap-6">
                        <Reveal>
                            <Card soft radius="lg" className="flex h-full flex-col justify-center gap-3">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[#0A0A0A]">
                                    <Icon name="receipt" className="h-[18px] w-[18px]" />
                                </span>
                                <div className="text-[17px] font-semibold tracking-[-0.01em] text-[#0A0A0A]">No incidents published</div>
                                <Body className="max-w-[380px]">
                                    Past incidents will be listed here by month once there are any to list. Until then this section stays empty rather than carrying an example written to fill the space.
                                </Body>
                            </Card>
                        </Reveal>
                        <Reveal delay={80}>
                            <div className="flex h-full flex-col gap-3">
                                <span className="text-[13px] font-medium text-[#A1A1A1]">What each post will contain</span>
                                <SpecList
                                    rows={[
                                        { label: 'Window', value: 'Start and end time' },
                                        { label: 'Scope', value: 'The components affected' },
                                        { label: 'Cause', value: 'What actually broke' },
                                        { label: 'Impact', value: 'What you would have seen' },
                                        { label: 'After', value: 'What changed so it does not repeat' },
                                    ]}
                                />
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={120}>
                        <div className="mt-6">
                            <Link to="/contact" className={BTN_SECONDARY}>
                                Ask for a status update
                                <Icon name="arrow" className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Reporting a problem ────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Reporting"
                            title="How to tell us something is wrong."
                            body="A store noticing it first is normal, not a failure. The faster the report carries the detail below, the faster it can be traced to the exact request that misbehaved."
                            bullets={[
                                'Name the location, and the app or the screen you were on',
                                'Give the time it happened, to the minute',
                                'Send the order number, or the day you were trying to close',
                                'A person answers, and the fix lands on the changelog when it ships',
                            ]}
                            cta={<Link to="/contact" className={BTN_SECONDARY}>Report a problem<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Diagram"
                                    label="A report travelling from the store to this page"
                                    note="Store, support, fix, status update, changelog entry, drawn as one line"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Security"
                title="How the platform is built."
                body="Where the data lives, who can reach it, what is encrypted and what is written to the audit log. The detail behind the state on this page."
                primary={{ label: 'Read the security page', to: '/security' }}
                secondary={{ label: 'Talk to us', to: '/contact' }}
            />
        </MarketingShell>
    );
}
